import React, { useState } from 'react';

const initialForm = {
  email: '',
  nombre: '',
  apellido: '',
  nombre_local: '',
  sucursal: '',
  tipo_plan: 'prueba',
  modo: 'fila'
};

function formatErrorDetail(detail) {
  if (!detail) {
    return '';
  }
  if (typeof detail === 'string') {
    return detail;
  }
  if (detail.message) {
    return String(detail.message);
  }
  if (Array.isArray(detail.cause) && detail.cause.length) {
    const firstCause = detail.cause[0];
    if (firstCause && firstCause.description) {
      return String(firstCause.description);
    }
  }
  return JSON.stringify(detail);
}

function Onboarding() {
  const [form, setForm] = useState(initialForm);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);
  const [recoveryEmail, setRecoveryEmail] = useState('');
  const [recoveryMessage, setRecoveryMessage] = useState('');
  const [recovering, setRecovering] = useState(false);

  const handleRecovery = async (event) => {
    event.preventDefault();
    if (!recoveryEmail || recovering) return;
    setRecovering(true);
    setRecoveryMessage('');
    try {
      const response = await fetch('/api/recuperar/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: recoveryEmail })
      });
      const json = await response.json();
      setRecoveryMessage(json.mensaje || json.error || 'Listo.');
    } catch (recoveryError) {
      setRecoveryMessage('No se pudo conectar con el servidor.');
    } finally {
      setRecovering(false);
    }
  };

  const handleChange = (event) => {
    const { name, value } = event.target;
    setForm((current) => ({
      ...current,
      [name]: value
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await fetch('/api/onboarding/', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(form)
      });

      const json = await response.json();
      if (!response.ok) {
        const detailText = formatErrorDetail(json.detail);
        const baseError = json.error || 'No se pudo crear el comercio.';
        setError(detailText ? `${baseError}. ${detailText}` : baseError);
        return;
      }

      setResult(json);

      if (json.requiere_pago_inmediato && json.payment_provider_url) {
        window.setTimeout(() => {
          window.location.assign(json.payment_provider_url);
        }, 900);
      }
    } catch (submitError) {
      setError('No se pudo conectar con el servidor.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={styles.page}>
      <div style={styles.hero}>
        <div style={styles.heroPanel}>
          <p style={styles.kicker}>Onboarding instantaneo</p>
          <h1 style={styles.title}>Alta tu comercio y empeza a dar turnos en segundos.</h1>
          <p style={styles.copy}>
            Ingresas tus datos, se crea el local, se genera el QR unico y te devolvemos el link del panel.
          </p>

          <div style={styles.switchPanel}>
            <p style={styles.switchTitle}>Tipo de negocio</p>
            <div style={styles.switchRow}>
              <button
                type="button"
                onClick={() => setForm((current) => ({ ...current, modo: 'fila' }))}
                style={{
                  ...styles.switchButton,
                  ...(form.modo === 'fila' ? styles.switchButtonActive : {})
                }}
              >
                🛍️ Fila de turnos (comercio)
              </button>
              <button
                type="button"
                onClick={() => setForm((current) => ({ ...current, modo: 'resto' }))}
                style={{
                  ...styles.switchButton,
                  ...(form.modo === 'resto' ? styles.switchButtonActive : {})
                }}
              >
                🍽️ Lista de espera (restaurante)
              </button>
            </div>
            <p style={styles.switchCopy}>
              {form.modo === 'fila'
                ? 'Tus clientes sacan un número y los llamás en orden (panadería, farmacia, atención al público).'
                : 'Los grupos se anotan con cantidad de personas y llamás al que entra en la mesa que se libera.'}
            </p>
          </div>

          <div style={styles.switchPanel}>
            <p style={styles.switchTitle}>Modo de alta</p>
            <div style={styles.switchRow}>
              <button
                type="button"
                onClick={() => setForm((current) => ({ ...current, tipo_plan: 'prueba' }))}
                style={{
                  ...styles.switchButton,
                  ...(form.tipo_plan === 'prueba' ? styles.switchButtonActive : {})
                }}
              >
                Prueba
              </button>
              <button
                type="button"
                onClick={() => setForm((current) => ({ ...current, tipo_plan: 'subscripcion' }))}
                style={{
                  ...styles.switchButton,
                  ...(form.tipo_plan === 'subscripcion' ? styles.switchButtonActive : {})
                }}
              >
                Subscripcion
              </button>
            </div>
            <p style={styles.switchCopy}>
              {form.tipo_plan === 'prueba'
                ? 'Activa 3 dias gratis y luego se bloquea hasta pagar $100.'
                : 'Se crea el comercio y te redirigimos a pagar $100 para habilitarlo.'}
            </p>
          </div>
        </div>

        <form style={styles.formCard} onSubmit={handleSubmit}>
          <label style={styles.label}>
            Email
            <input name="email" type="email" value={form.email} onChange={handleChange} style={styles.input} required />
          </label>

          <label style={styles.label}>
            Nombre
            <input name="nombre" value={form.nombre} onChange={handleChange} style={styles.input} required />
          </label>

          <label style={styles.label}>
            Apellido
            <input name="apellido" value={form.apellido} onChange={handleChange} style={styles.input} required />
          </label>

          <label style={styles.label}>
            Nombre del local
            <input name="nombre_local" value={form.nombre_local} onChange={handleChange} style={styles.input} required />
          </label>

          <label style={styles.label}>
            Sucursal
            <input name="sucursal" value={form.sucursal} onChange={handleChange} style={styles.input} required />
          </label>

          <button type="submit" style={styles.button} disabled={loading}>
            {loading ? 'Creando...' : 'Crear comercio'}
          </button>

          {error && <p style={styles.error}>{error}</p>}
        </form>
      </div>

      {result && (
        <div style={styles.resultCard}>
          <div>
            <p style={styles.resultKicker}>Comercio listo</p>
            <h2 style={styles.resultTitle}>{result.nombre_local}</h2>
            <p style={styles.resultCopy}>Sucursal: ({result.sucursal})</p>
            <p style={styles.resultCopy}>Cliente: <a href={result.client_url}>{result.client_url}</a></p>
            <p style={styles.resultCopy}>Admin: <a href={result.admin_url}>{result.admin_url}</a></p>
            {result.display_url && <p style={styles.resultCopy}>Pantalla TV: <a href={result.display_url}>{result.display_url}</a></p>}
            <p style={styles.resultCopy}>Plan: {result.tipo_plan}</p>
            <p style={styles.resultCopy}>Estado: {result.estado_comercio}</p>
            {result.payment_provider_url && <p style={styles.resultCopy}>Checkout: <a href={result.payment_provider_url}>{result.payment_provider_url}</a></p>}
            <p style={styles.resultCopy}>{result.email_enviado ? 'Email enviado correctamente.' : 'No se pudo enviar el email automaticamente.'}</p>
            {result.requiere_pago_inmediato && <p style={styles.resultCopy}>Redirigiendo a la pantalla de pago...</p>}
          </div>

          {result.qr_base64 && (
            <img
              alt={`QR de ${result.nombre_local}`}
              src={`data:image/png;base64,${result.qr_base64}`}
              style={styles.qr}
            />
          )}
        </div>
      )}

      <div style={styles.recoveryCard}>
        <p style={styles.recoveryTitle}>¿Ya tenés tu comercio y perdiste los links?</p>
        <form style={styles.recoveryForm} onSubmit={handleRecovery}>
          <input
            type="email"
            placeholder="Tu email"
            value={recoveryEmail}
            onChange={(e) => setRecoveryEmail(e.target.value)}
            style={styles.recoveryInput}
            required
          />
          <button type="submit" style={styles.recoveryButton} disabled={recovering}>
            {recovering ? 'Enviando…' : 'Reenviar accesos'}
          </button>
        </form>
        {recoveryMessage && <p style={styles.recoveryMessage}>{recoveryMessage}</p>}
      </div>
    </div>
  );
}

const styles = {
  page: {
    minHeight: '100vh',
    padding: '32px',
    background: 'linear-gradient(135deg, #f3efe6 0%, #dbe7d8 100%)',
    fontFamily: 'Georgia, serif',
    color: '#1f2a1f'
  },
  hero: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
    gap: '24px',
    alignItems: 'start'
  },
  heroPanel: {
    padding: '24px',
    background: 'rgba(255,255,255,0.55)',
    borderRadius: '24px',
    backdropFilter: 'blur(6px)'
  },
  kicker: {
    textTransform: 'uppercase',
    letterSpacing: '0.2em',
    fontSize: '12px',
    marginBottom: '16px'
  },
  title: {
    fontSize: '48px',
    lineHeight: 1,
    margin: '0 0 16px'
  },
  copy: {
    fontSize: '18px',
    lineHeight: 1.5,
    margin: 0
  },
  switchPanel: {
    marginTop: '28px',
    padding: '18px',
    borderRadius: '18px',
    background: 'rgba(31, 42, 31, 0.08)'
  },
  switchTitle: {
    marginTop: 0,
    marginBottom: '12px',
    fontWeight: 'bold'
  },
  switchRow: {
    display: 'flex',
    gap: '12px',
    flexWrap: 'wrap'
  },
  switchButton: {
    padding: '12px 18px',
    borderRadius: '999px',
    border: '1px solid #1f2a1f',
    background: 'transparent',
    color: '#1f2a1f',
    cursor: 'pointer'
  },
  switchButtonActive: {
    background: '#1f2a1f',
    color: '#fff8ee'
  },
  switchCopy: {
    marginBottom: 0,
    marginTop: '12px',
    fontSize: '15px',
    lineHeight: 1.5
  },
  formCard: {
    display: 'grid',
    gap: '16px',
    padding: '24px',
    background: '#1f2a1f',
    color: '#f8f4ea',
    borderRadius: '24px',
    boxShadow: '0 24px 80px rgba(31, 42, 31, 0.25)'
  },
  label: {
    display: 'grid',
    gap: '8px',
    fontSize: '14px'
  },
  input: {
    padding: '14px 16px',
    borderRadius: '12px',
    border: '1px solid rgba(248,244,234,0.2)',
    background: '#f8f4ea',
    color: '#1f2a1f',
    fontSize: '16px'
  },
  button: {
    padding: '16px',
    borderRadius: '999px',
    border: 'none',
    background: '#d88c45',
    color: '#fff8ee',
    fontSize: '16px',
    cursor: 'pointer'
  },
  error: {
    color: '#ffcfbf',
    margin: 0
  },
  resultCard: {
    marginTop: '32px',
    padding: '24px',
    borderRadius: '24px',
    background: '#fffdf7',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: '24px',
    flexWrap: 'wrap'
  },
  resultKicker: {
    textTransform: 'uppercase',
    letterSpacing: '0.15em',
    fontSize: '12px'
  },
  resultTitle: {
    margin: '8px 0 16px',
    fontSize: '32px'
  },
  resultCopy: {
    margin: '6px 0',
    wordBreak: 'break-word'
  },
  qr: {
    width: '220px',
    height: '220px',
    borderRadius: '24px',
    background: '#fff',
    padding: '12px'
  },
  recoveryCard: {
    marginTop: '32px',
    padding: '20px 24px',
    borderRadius: '18px',
    background: 'rgba(255,255,255,0.55)',
    maxWidth: '520px'
  },
  recoveryTitle: {
    marginTop: 0,
    marginBottom: '12px',
    fontWeight: 'bold'
  },
  recoveryForm: {
    display: 'flex',
    gap: '10px',
    flexWrap: 'wrap'
  },
  recoveryInput: {
    flex: '1 1 220px',
    padding: '12px 14px',
    borderRadius: '12px',
    border: '1px solid #9aa79a',
    fontSize: '15px'
  },
  recoveryButton: {
    padding: '12px 20px',
    borderRadius: '999px',
    border: 'none',
    background: '#1f2a1f',
    color: '#fff8ee',
    fontSize: '15px',
    cursor: 'pointer'
  },
  recoveryMessage: {
    marginBottom: 0,
    marginTop: '12px',
    fontSize: '14px'
  }
};

export default Onboarding;